import { getCurrentInstance } from "vue";
import { AdminPortal } from "@frontegg/vue";
import type { FronteggAppOptions, FronteggThemeOptions } from "@frontegg/types";
import { useDarkModeStore } from "@/stores/darkMode.store";

type AppWithFrontegg = { $fronteggApp?: { options: FronteggAppOptions } };

const token = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

// The portal reads only themeOptions.adminPortal; its built-in dark surface is a fixed #232526, so the
// page (common.black), greys and text are pinned to the app's dark tokens.
const darkPortalTheme = (): FronteggThemeOptions => ({
  adminPortal: {
    themeName: "dark",
    palette: {
      primary: {
        main: token("--color-app-blue-400"),
        light: token("--color-app-blue-600"),
        dark: token("--color-app-blue-fill"),
        hover: token("--color-app-blue-500"),
        active: token("--color-app-blue-400"),
      },
      common: { black: token("--color-elevated"), white: token("--color-type-default") },
      text: {
        primary: token("--color-type-default"),
        secondary: token("--color-type-muted"),
        disabled: token("--color-type-muted"),
      },
      grey: {
        "200": token("--color-type-muted"),
        "400": token("--color-type-muted"),
        "600": `color-mix(in srgb, ${token("--color-type-muted")} 80%, ${token("--color-elevated")})`,
        "700": `color-mix(in srgb, ${token("--color-type-muted")} 80%, ${token("--color-elevated")})`,
        "800": token("--color-slate-100"),
      },
      divider: token("--color-slate-200"),
    },
  },
});

// Frontegg builds the portal from app.options.themeOptions each time it opens, so set them just before.
export function useAdminPortal() {
  const darkModeStore = useDarkModeStore();
  const app = getCurrentInstance()?.appContext.app as AppWithFrontegg | undefined;

  const show = () => {
    if (app?.$fronteggApp) app.$fronteggApp.options.themeOptions = darkModeStore.isDark ? darkPortalTheme() : undefined;
    AdminPortal.show();
  };

  return { show };
}
